import React, { useState, useEffect, useCallback } from 'react';
import '../IpPatientDetails.css';
import {
  Autocomplete, Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { CommonPatientDetails, CommonPatientCard, ConsultantPatientCard, CommonEditButton, CommonDeleteButton, DeletePopup } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';
import { Colors } from '../../../../../Styles/Colors';
import { ImagePaths } from '../../../../../Utility/ImagePaths';
import CommonValidation from "../../../../../Components/CommonFunctions/CommonValidation";

const TASK_TYPE_OPTIONS = [{ value: 'Care Task', label: 'Care Task' }, { value: 'Treatment Task', label: 'Treatment Task' }];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const PRIORITY_COLOR = { High: 'error', Medium: 'warning', Low: 'info' };
const STATUS_COLOR = { Pending: 'warning', Completed: 'success' };

export default function SpecialTask(props) {
  const { t } = useTranslation();
  const [patientData, setPatientData] = useState({});
  const [admissionDetails, setAdmissionDetails] = useState({});
  const [taskList, setTaskList] = useState([]);
  const [stationList, setStationList] = useState([]);
  const [patientStation, setPatientStation] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    task_name: '',
    instructions: '',
    priority: 'High',
    task_datetime: DateTime.now().toFormat('yyyy-MM-dd') + 'T' + DateTime.now().toFormat('HH:mm'),
    task_type: 'Care Task',
    station_id: '',
    nurse_id: '',
  });
  const [therapyOptions, setTherapyOptions] = useState([]);
  const [drugFieldOptions, setDrugFieldOptions] = useState({});
  const [selectedTherapy, setSelectedTherapy] = useState(null);
  const [treatmentDrugs, setTreatmentDrugs] = useState([]);
  const [deletedDrugIds, setDeletedDrugIds] = useState([]);

  const COMBO_COLORS = ['gray', Colors.Primary || '#4da3d4'];
  const getComboColor = (drugIdx) => COMBO_COLORS[drugIdx % 2];
  const emptyCombo = () => ({ id: null, drug_id: null, drug_name: '', quantity: '', uom: 'nos', is_billable: false, billable_qty: 0, comboKey: `c-${Date.now()}` });
  const emptyDrugRow = (comboGroup = null) => ({ id: null, drug_id: null, drug_name: '', quantity: '', uom: 'nos', is_billable: false, billable_qty: 0, combination_group: comboGroup, combinations: [] });

  const ip_admission_id = patientData?.id || admissionDetails?.ip_admission_id;
  const clinic_id = JSON.parse(localGetItem('SelectedDoctorClinicId') || 'null');

  const fetchPatient = useCallback(() => {
    const raw = getCachevalue('IppatientData');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      setPatientData(data);
    } catch (e) { }
  }, []);

  const fetchAdmissionDetails = useCallback(() => {
    if (!ip_admission_id) return;
    const url = props.fromWalkin
      ? `${Serviceurls.AYURVEDA_PATIENT_HEADER}?walkin_admission_id=${ip_admission_id}`
      : `${Serviceurls.FO_PATIENT_DETAILS_NEW}?admission_id=${ip_admission_id}`;
    const fullUrl = (props.is_From_IpNotes || props.fromWalkin) && clinic_id ? `${url}&clinic_id=${clinic_id}` : url;
    RestAPIService.getAll(fullUrl).then((res) => {
      if (res.data?.status === 'success' && res.data?.data?.[0]) setAdmissionDetails(res.data.data[0]);
    }).catch(() => { });
  }, [ip_admission_id, props.fromWalkin, props.is_From_IpNotes, clinic_id]);

  const fetchTaskList = useCallback(() => {
    if (!ip_admission_id) return;
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_NURSE_SPECIAL_TASK}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status === 'success') setTaskList(res.data?.data || []);
        else setTaskList([]);
        setIsLoader(false);
      })
      .catch(() => { setTaskList([]); setIsLoader(false); });
  }, [ip_admission_id]);

  const fetchStationList = useCallback(() => {
    if (!ip_admission_id) return;
    RestAPIService.getAll(`${Serviceurls.IP_STATION_NURSE_LIST}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status !== 'success') return;
        const data = res.data?.data || [];
        const ps = res.data?.patient_station || null;
        const defaultStation = ps || data[0] || null;
        const defaultNurse = defaultStation?.data?.[0] || null;
        setStationList(data);
        setPatientStation(ps);
        setForm((prev) => ({
          ...prev,
          station_id: defaultStation?.station_id ?? '',
          nurse_id: defaultNurse?.nurse_id ?? '',
        }));
      })
      .catch(() => { });
  }, [ip_admission_id]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  useEffect(() => {
    if (ip_admission_id) {
      fetchAdmissionDetails();
      fetchTaskList();
      fetchStationList();
    }
  }, [ip_admission_id, fetchAdmissionDetails, fetchTaskList, fetchStationList]);

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const fetchTherapySearch = (key) => {
    if (!key || String(key).trim().length < 2) return;
    RestAPIService.getAll(`${Serviceurls.AYURVEDA_THERAPY_SEARCH}?treatment_name=${encodeURIComponent(key)}`)
      .then((res) => { if (res.data?.status === 'success') setTherapyOptions(res.data?.data || []); })
      .catch(() => setTherapyOptions([]));
  };

  const fetchTreatmentDetails = (serviceChargeId) => {
    if (!serviceChargeId) return;
    RestAPIService.getAll(`${Serviceurls.AYURVEDA_TREATMENT_MEDICINE_GET}?service_charge_id=${serviceChargeId}`)
      .then((res) => {
        if (res.data?.status !== 'success' || !res.data?.data) return;
        const raw = res.data.data;
        const d = Array.isArray(raw) ? raw[0] : raw;
        if (!d) return;
        setSelectedTherapy({
          service_charge_id: d.service_charge_id,
          treatment_ms_id: d.treatment_ms_id,
          therapy_name: d.treatment_name || form.task_name,
          duration: d.duration ?? 15,
          duration_mode: d.duration_mode || 'Minutes',
        });
        setForm((f) => ({ ...f, task_name: d.treatment_name || f.task_name }));
        if (d.drugs && Array.isArray(d.drugs) && d.drugs.length > 0) {
          setTreatmentDrugs(d.drugs.map((dr, drIdx) => ({
            id: dr.id ?? null,
            drug_id: dr.drug_id,
            drug_name: dr.drug_name || '',
            quantity: dr.quantity ?? '',
            uom: dr.uom || dr.drug_uom || 'nos',
            is_billable: dr.is_billable === true,
            billable_qty: dr.billable_qty ?? 0,
            combination_group: null,
            combinations: (dr.combinations || []).map((c, cIdx) => ({
              id: c.id ?? null,
              drug_id: c.drug_id,
              drug_name: c.drug_name || '',
              quantity: c.quantity ?? '',
              uom: c.uom || 'nos',
              is_billable: c.is_billable === true,
              billable_qty: c.billable_qty ?? 0,
              comboKey: c.comboKey || `c-${drIdx}-${cIdx}-${Date.now()}`,
            })),
          })));
        } else {
          setTreatmentDrugs([emptyDrugRow()]);
        }
      })
      .catch(() => { setSelectedTherapy(null); setTreatmentDrugs([emptyDrugRow()]); });
  };

  const fetchDrugSearch = (key, fieldKey) => {
    if (!fieldKey || !key || String(key).trim().length < 2) return;
    RestAPIService.getAll(`${Serviceurls.AYURVEDA_MEDICINE_PREPARE_DRUG_SEARCH}?search_key=${encodeURIComponent(key)}`)
      .then((res) => {
        if (res.data?.status === 'success') setDrugFieldOptions((prev) => ({ ...prev, [fieldKey]: res.data?.data || [] }));
      })
      .catch(() => setDrugFieldOptions((prev) => ({ ...prev, [fieldKey]: [] })));
  };

  const addMedicineRow = () => setTreatmentDrugs((prev) => [...prev, emptyDrugRow()]);
  const addCombination = (drugIdx) => {
    setTreatmentDrugs((prev) =>
      prev.map((d, i) =>
        i === drugIdx
          ? { ...d, combinations: [...(d.combinations || []), emptyCombo()] }
          : d
      )
    );
  };
  const updateDrug = (drugIdx, updates, isComb = false, combIdx = null) => {
    if (updates.quantity && updates.quantity.length > 5) {
      return;
    }
    setTreatmentDrugs((prev) => {
      const next = prev.map((d, i) => {
        if (i !== drugIdx) return d;
        if (isComb && combIdx !== null && d.combinations) {
          const c = [...d.combinations];
          c[combIdx] = { ...c[combIdx], ...updates };
          return { ...d, combinations: c };
        }
        return { ...d, ...updates };
      });
      return next;
    });
  };
  const removeDrugRow = (idx) => {
    const row = treatmentDrugs[idx];
    if (row?.id) setDeletedDrugIds((prev) => [...prev, row.id]);
    setTreatmentDrugs((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeCombination = (drugIdx, combIdx) => {
    const row = treatmentDrugs[drugIdx];
    const comb = row?.combinations?.[combIdx];
    if (comb?.id) setDeletedDrugIds((prev) => [...prev, comb.id]);
    setTreatmentDrugs((prev) =>
      prev.map((d, i) =>
        i === drugIdx
          ? { ...d, combinations: (d.combinations || []).filter((_, ci) => ci !== combIdx) }
          : d
      )
    );
  };

  const handleClear = () => {
    setEditingId(null);
    setSelectedTherapy(null);
    setTreatmentDrugs([]);
    setDeletedDrugIds([]);
    const defaultStation = patientStation || stationList[0];
    const defaultNurse = defaultStation?.data?.[0];
    setForm({
      task_name: '',
      instructions: '',
      priority: 'High',
      task_datetime: DateTime.now().toFormat('yyyy-MM-dd') + 'T' + DateTime.now().toFormat('HH:mm'),
      task_type: 'Care Task',
      station_id: defaultStation?.station_id ?? '',
      nurse_id: defaultNurse?.nurse_id ?? '',
    });
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    const dt = row.task_datetime ? row.task_datetime.replace(/\s/g, ' ').split(' ') : [];
    let taskDatetime = '';
    if (dt.length >= 2) {
      const d = dt[0].split('-');
      if (d.length === 3) taskDatetime = `${d[2]}-${d[1]}-${d[0]}T${dt[1]}`;
    }
    if (!taskDatetime && row.task_datetime) taskDatetime = row.task_datetime;
    setForm({
      task_name: row.task_name || '',
      instructions: row.instructions || '',
      priority: row.priority || 'High',
      task_datetime: taskDatetime || DateTime.now().toFormat('yyyy-MM-dd\'T\'HH:mm'),
      task_type: row.task_type || row.task_ype || 'Care Task',
      station_id: row.station_id ?? '',
      nurse_id: row.nurse_id ?? '',
    });
    const isTreatment = (row.task_type || row.task_ype || '').toLowerCase().includes('treatment');
    if (isTreatment && row.drugs && Array.isArray(row.drugs)) {
      setSelectedTherapy({
        service_charge_id: row.service_charge_id,
        treatment_ms_id: row.treatment_ms_id,
        therapy_name: row.task_name,
        duration: row.duration ?? 15,
        duration_mode: row.duration_mode || 'Minutes',
      });
      setTreatmentDrugs(row.drugs.map((dr, drIdx) => ({
        id: dr.id ?? null,
        drug_id: dr.drug_id,
        drug_name: dr.drug_name || '',
        quantity: dr.quantity ?? '',
        uom: dr.uom || 'nos',
        is_billable: dr.is_billable === true,
        billable_qty: dr.billable_qty ?? 0,
        combination_group: null,
        combinations: (dr.combinations || []).map((c, cIdx) => ({
          id: c.id ?? null,
          drug_id: c.drug_id,
          drug_name: c.drug_name || '',
          quantity: c.quantity ?? '',
          uom: c.uom || 'nos',
          is_billable: c.is_billable === true,
          billable_qty: c.billable_qty ?? 0,
          comboKey: c.comboKey || `c-${drIdx}-${cIdx}-${Date.now()}`,
        })),
      })));
      setDeletedDrugIds([]);
    } else {
      setSelectedTherapy(null);
      setTreatmentDrugs([]);
      setDeletedDrugIds([]);
    }
  };

  const validate = () => {
    if (!form.task_name?.trim()) { showError('Task name is required.'); return false; }
    if (!form.task_datetime) { showError('Task date & time is required.'); return false; }
    if (!form.station_id) { showError('Select nurse station.'); return false; }
    const isTreatment = form.task_type === 'Treatment Task';
    if (isTreatment && !selectedTherapy?.service_charge_id) { showError('Select a therapy from the list.'); return false; }
    if (isTreatment && (!treatmentDrugs?.length || !treatmentDrugs.some((d) => d.drug_id && (d.quantity !== '' && d.quantity != null)))) { showError('Add at least one medicine with quantity.'); return false; }
    if (isTreatment && treatmentDrugs?.length > 0) {
      for (let i = 0; i < treatmentDrugs.length; i++) {
        const d = treatmentDrugs[i];
        if (d.drug_id && (d.quantity === '' || d.quantity == null)) { showError(`Enter quantity for medicine: ${d.drug_name || 'Main drug'}.`); return false; }
        if ((d.combinations || []).some((c) => c.drug_id && (c.quantity === '' || c.quantity == null))) { showError('Enter quantity for all combination drugs.'); return false; }
      }
    }
    return true;
  };

  const formatTaskDatetimeForApi = (val) => {
    if (!val) return '';
    const str = val.replace('T', ' ');
    if (str.length <= 16) return str + ':00';
    return str;
  };

  const buildTreatmentDrugsPayload = () => {
    return treatmentDrugs
      .filter((d) => d.drug_id && (d.quantity !== '' && d.quantity != null))
      .map((d) => ({
        id: d.id,
        drug_id: d.drug_id,
        quantity: Number(d.quantity) || 0,
        uom: d.uom || 'nos',
        is_billable: d.is_billable === true,
        billable_qty: Number(d.billable_qty) || 0,
        combinations: (d.combinations || []).map((c) => ({
          id: c.id,
          drug_id: c.drug_id,
          quantity: Number(c.quantity) || 0,
          uom: c.uom || 'nos',
          is_billable: c.is_billable === true,
          billable_qty: Number(c.billable_qty) || 0,
        })),
      }));
  };

  const handleSubmit = () => {
    if (!validate() || !ip_admission_id) return;
    const doctor_id = admissionDetails?.doctor_id || (() => { try { return JSON.parse(localGetItem('loggedInUserInfo') || '{}')?.doctor_id; } catch (e) { return null; } })();
    const isTreatment = form.task_type === 'Treatment Task';
    const basePayload = {
      id: editingId || null,
      task_type: form.task_type,
      task_ype: form.task_type,
      task_name: form.task_name.trim(),
      instructions: (form.instructions || '').slice(0, 250),
      priority: form.priority,
      task_datetime: formatTaskDatetimeForApi(form.task_datetime),
      station_id: Number(form.station_id),
      nurse_id: form.nurse_id ? Number(form.nurse_id) : null,
      ip_admission_id,
      doctor_id: doctor_id ?? null,
    };
    const payload = isTreatment && selectedTherapy
      ? {
          ...basePayload,
          service_charge_id: selectedTherapy.service_charge_id,
          treatment_ms_id: selectedTherapy.treatment_ms_id,
          duration: Number(selectedTherapy.duration) || 15,
          duration_mode: selectedTherapy.duration_mode || 'Minutes',
          deleted_drug_id: deletedDrugIds,
          drugs: buildTreatmentDrugsPayload(),
        }
      : basePayload;

    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.IP_NURSE_SPECIAL_TASK)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(editingId ? res.data?.message : (isTreatment ? 'Treatment task added.' : 'Task added.'));
          handleClear();
          fetchTaskList();
        } else showError(res.data?.message || 'Failed to save.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to save.');
      });
  };

  const handleDelete = () => {
    if (!deleteId) return;
    setIsLoader(true);
    RestAPIService.delete(`${Serviceurls.IP_NURSE_SPECIAL_TASK}?id=${deleteId}`, {})
      .then((res) => {
        setIsLoader(false);
        setDeletePopup(false);
        setDeleteId(null);
        showSuccess(res?.data?.message || 'Task deleted.');
        fetchTaskList();
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to delete.');
      });
  };

  const attenderData = {
    lineOne: t('AttenderName'),
    lineTwo: admissionDetails?.attender_name || '-',
    lineFour: t('ContactNumber'),
    LineFive: admissionDetails?.attender_mobile || '-',
  };
  const consultantData = {
    image: ImagePaths.Doctor_Icon?.default,
    height: '2.3vw',
    width: '2.3vw',
    lineOne: t('ConsultantName'),
    lineTwo: admissionDetails?.doctor_name || '-',
  };

  const currentStation = stationList.find((s) => Number(s.station_id) === Number(form.station_id));
  const nurses = currentStation?.data || [];

  return (
    <Box sx={{ backgroundColor: Colors.configHomeBg, minHeight: '74dvh', p: '0.625vw' }}>
      <Grid container className="" >
        <Stack className="eMed_medicationCard_header" direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-evenly" alignItems="center" spacing={1.2} >
            <Stack className="eMed_recCard_div" sx={{ width: "23vw", backgroundColor: Colors.white }} >
              <CommonPatientDetails data={patientData} showDetailed={true} />
            </Stack>
            <CommonPatientCard details={attenderData} showDetailed={true} />
            <ConsultantPatientCard name={consultantData} width={"20dvw"} />
          </Stack>
        </Stack>
      </Grid>
      <Box component={'div'} height={'64dvh'} overflow={'auto'}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ overflow: 'auto', height: '60dvh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date and Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Task Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Instructions</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Task Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Task Date and Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Current Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskList.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.created_date || '-'}</TableCell>
                      <TableCell>{row.task_name || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>{(row.instructions || '-').length > 15 ? (row.instructions || '').slice(0, 15) + '...' : (row.instructions || '-')}</TableCell>
                      <TableCell><Chip size="small" label={row.priority || '-'} color={PRIORITY_COLOR[row.priority] || 'default'} /></TableCell>
                      <TableCell>{row.task_type || row.task_ype || '-'}</TableCell>
                      <TableCell>{row.task_datetime || '-'}</TableCell>
                      <TableCell><Chip size="small" label={row.status || 'Pending'} color={STATUS_COLOR[row.status] || 'default'} /></TableCell>
                      <TableCell align="center">
                        <CommonEditButton size="1.5vw" onClick={() => handleEdit(row)} />
                        <CommonDeleteButton size="1.5vw" onClick={() => { setDeleteId(row.id); setDeletePopup(true); }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {taskList.length === 0 && !isLoader && (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{t('NoRecordsFound')}</Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Add Task</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Task Type</InputLabel>
                    <Select value={form.task_type} label="Task Type" onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, task_type: v }));
                      if (v === 'Care Task') { setSelectedTherapy(null); setTreatmentDrugs([]); setDeletedDrugIds([]); }
                    }}>
                      {TASK_TYPE_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {form.task_type === 'Treatment Task' ? (
                    <Autocomplete
                      size="small"
                      options={therapyOptions}
                      getOptionLabel={(o) => o?.service_name || o?.treatment_name || o?.therapy_name || ''}
                      value={therapyOptions.find((o) => o.service_charge_id === selectedTherapy?.service_charge_id) || (form.task_name ? { treatment_name: form.task_name } : null)}
                      onInputChange={(e, v) => fetchTherapySearch(v)}
                      onChange={(e, v) => {
                        if (v) {
                          setForm((f) => ({ ...f, task_name: v.treatment_name || v.therapy_name || v.service_name || '' }));
                          fetchTreatmentDetails(v.service_charge_id);
                        } else {
                          setForm((f) => ({ ...f, task_name: '' }));
                          setSelectedTherapy(null);
                          setTreatmentDrugs([]);
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label="Task Name (Therapy)" />}
                    />
                  ) : (
                    <TextField fullWidth size="small" label="Task Name" value={form.task_name} onChange={(e) => setForm((f) => ({ ...f, task_name: e.target.value }))} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Instructions" placeholder="Enter Description within 250 characters" value={form.instructions} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value.slice(0, 250) }))} multiline rows={2} />
                </Grid>
                {form.task_type === 'Treatment Task' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Medicine</Typography>
                    {treatmentDrugs.map((drug, drugIdx) => {
                      const borderColor = getComboColor(drugIdx);
                      const rowBg = `${borderColor}20`;
                      return (
                        <Box key={drugIdx} sx={{ mb: 1.5 }}>
                          {/* Main drug row */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1, borderLeft: `4px solid ${borderColor}`, bgcolor: rowBg, borderRadius: 1 }}>
                            <IconButton size="small" onClick={() => addCombination(drugIdx)} title="Add combination" sx={{ border: `1px solid ${borderColor}`, color: borderColor, '&:hover': { bgcolor: borderColor, color: '#fff' } }}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <Autocomplete
                              size="small"
                              options={drugFieldOptions[`main-${drugIdx}`] ?? []}
                              getOptionLabel={(o) => o?.drug_name || ''}
                              value={(drugFieldOptions[`main-${drugIdx}`] ?? []).find((o) => o.id === drug.drug_id) || (drug.drug_name ? { id: drug.drug_id, drug_name: drug.drug_name, uom: drug.uom } : null)}
                              onInputChange={(e, v) => fetchDrugSearch(v, `main-${drugIdx}`)}
                              onChange={(e, v) => updateDrug(drugIdx, { drug_id: v?.id ?? null, drug_name: v?.drug_name ?? '', uom: v?.uom ?? 'nos' })}
                              renderInput={(params) => <TextField {...params} label="Medicine" />}
                              sx={{ minWidth: 140, flex: 1 }}
                            />
                            <TextField size="small" label="Qty" value={drug.quantity} onChange={(e) => updateDrug(drugIdx, { quantity: e.target.value })} type="number" InputProps={{  maxLength: 5}} sx={{ width: 72 }} />
                            <TextField size="small" label="UOM" value={drug.uom} onChange={(e) => updateDrug(drugIdx, { uom: e.target.value })} sx={{ width: 64 }} />
                            {treatmentDrugs.length > 1 && <IconButton size="small" onClick={() => removeDrugRow(drugIdx)}><CloseIcon fontSize="small" /></IconButton>}
                          </Stack>
                          {/* Combination rows: one per row with same border */}
                          {(drug.combinations || []).map((comb, cIdx) => (
                            <Stack key={cIdx} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, pl: 2, py: 0.75, borderLeft: `4px solid ${borderColor}`, bgcolor: rowBg, borderRadius: 1 }}>
                              <Box sx={{ width: 32 }} />
                              <Autocomplete
                                size="small"
                                options={drugFieldOptions[`combo-${drugIdx}-${comb.comboKey || cIdx}`] ?? []}
                                getOptionLabel={(o) => o?.drug_name || ''}
                                value={(drugFieldOptions[`combo-${drugIdx}-${comb.comboKey || cIdx}`] ?? []).find((o) => o.id === comb.drug_id) || (comb.drug_name ? { id: comb.drug_id, drug_name: comb.drug_name, uom: comb.uom } : null)}
                                onInputChange={(e, v) => fetchDrugSearch(v, `combo-${drugIdx}-${comb.comboKey || cIdx}`)}
                                onChange={(e, v) => updateDrug(drugIdx, { drug_id: v?.id, drug_name: v?.drug_name || '', uom: v?.uom || 'nos' }, true, cIdx)}
                                renderInput={(params) => <TextField {...params} size="small" placeholder="Combination drug" />}
                                sx={{ minWidth: 140, flex: 1 }}
                              />
                              <TextField size="small" label="Qty" value={comb.quantity} onChange={(e) => updateDrug(drugIdx, { quantity: e.target.value }, true, cIdx)} type="number" InputProps={{  maxLength: 5}} sx={{ width: 72 }} />
                              <TextField size="small" label="UOM" value={comb.uom} onChange={(e) => updateDrug(drugIdx, { uom: e.target.value }, true, cIdx)} sx={{ width: 64 }} />
                              <IconButton size="small" onClick={() => removeCombination(drugIdx, cIdx)}><CloseIcon fontSize="small" /></IconButton>
                            </Stack>
                          ))}
                        </Box>
                      );
                    })}
                    <Button size="small" startIcon={<AddIcon />} onClick={addMedicineRow} sx={{ mt: 0.5, textTransform: 'capitalize' }}>Add medicine</Button>
                  </Grid>
                )}
                <Grid item xs={12} display={'flex'} gap={1}>
                  <Grid item xs={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority Level</InputLabel>
                    <Select value={form.priority} label="Priority Level" onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                      {PRIORITY_OPTIONS.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                    </Select>
                  </FormControl>
                  </Grid>
                  <Grid item xs={7}>
                  <TextField fullWidth size="small" type="datetime-local" label="Task Date & Time" value={form.task_datetime || ''} onChange={(e) => setForm((f) => ({ ...f, task_datetime: e.target.value }))} InputLabelProps={{ shrink: true }} />
                </Grid>
                </Grid>
               <Grid item xs={12} display={'flex'} gap={1}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Nurse Station</InputLabel>
                    <Select value={form.station_id} label="Nurse Station" onChange={(e) => setForm((f) => ({ ...f, station_id: e.target.value, nurse_id: '' }))}>
                      <MenuItem value="">Select</MenuItem>
                      {stationList.map((s) => (<MenuItem key={s.station_id} value={s.station_id}>{s.station_name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Nurse</InputLabel>
                    <Select value={form.nurse_id} label="Nurse" onChange={(e) => setForm((f) => ({ ...f, nurse_id: e.target.value }))}>
                      <MenuItem value="">Select</MenuItem>
                      {nurses.map((n) => (<MenuItem key={n.nurse_id} value={n.nurse_id}>{n.nurse_name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
                  <Button variant="outlined" onClick={handleClear} sx={{ textTransform: 'uppercase' }}>Clear</Button>
                  <Button variant="contained" onClick={handleSubmit} sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary }}>{editingId ? 'Update Task' : 'Add Task'}</Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {deletePopup && (
        <DeletePopup
          DeletTitle="Delete this task?"
          deleteAlertPopupClose={() => { setDeletePopup(false); setDeleteId(null); }}
          removeData={handleDelete} />
      )}
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
